import { VertexBuffer } from '@babylonjs/core/Buffers/buffer';
import { MultiMaterial } from '@babylonjs/core/Materials/multiMaterial';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import { Vector3 } from '@babylonjs/core/Maths/math';
import { AbstractMesh } from '@babylonjs/core/Meshes/abstractMesh';
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { SubMesh } from '@babylonjs/core/Meshes/subMesh';
import { Logger } from '@babylonjs/core/Misc/logger';
import { Observable } from '@babylonjs/core/Misc/observable';
import { UtilityLayerRenderer } from '@babylonjs/core/Rendering/utilityLayerRenderer';
import { Scene } from '@babylonjs/core/scene';
import { Nullable } from '@babylonjs/core/types';
import { IVertexGroup } from '@kb3d/models/classes/kb3d-models';
import { MeshNode } from '@kb3d/models/classes/meshes/mesh-node';
import { KbColor } from '@kb3d/models/classes/primitives/kb-color';
import { KbViewer } from '@kb3d/view/kb-viewer';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MeshFaceEngine } from '../face-engine';
import { eMarqueeSelectMode } from '../interaction';
import { KbGizmo } from './gizmo';

export interface VertexChanges {
    mesh: AbstractMesh;
    delta: Vector3;
    vertexIndices: number[];
}
const dbSubmeshes = new Map<
    string,
    {
        visualizationMesh: Mesh;
        highlightMaterial: StandardMaterial;
        vertices: IVertexGroup[];
    }
>();

/**
 * Gizmo that enables moving vertices on a mesh.
 */
export class KbFacetSelectGizmo extends KbGizmo {
    /** Fires an event when any of it's sub gizmos are dragged */
    public onSelectionChange = new Observable<{ selectedFacets: Array<Required<IVertexGroup>> }>();

    public get attachedMesh() {
        return this.__attachedMesh;
    }
    public set attachedMesh(mesh: Nullable<AbstractMesh>) {
        if (mesh && !(mesh instanceof Mesh)) {
            throw Error('facet select gizmo must be attached to a mesh with geometry');
        }
        this.__attachedMesh = mesh instanceof Mesh ? mesh : null;
    }

    private utilLayer: UtilityLayerRenderer;
    private meshFaceEngine: MeshFaceEngine;
    private disposeSubject: Subject<void>;
    private __attachedNode: Nullable<MeshNode>;
    private __attachedMesh: Nullable<Mesh> = null;

    private currentSelection = new Map<number, null>();
    private selectionsEnabled = false;
    private highlightOpacity = 0.75;
    private selectCoplanarEnabled = false;
    private backfaceCullingEnabled = true;
    private selectionTolerance = 0;

    /**
     * Creates a FacetSelectGizmo
     * @param kbViewer The viewer instance
     * @param scene The scene
     */
    constructor(private kbViewer: KbViewer, private scene: Scene) {
        super(kbViewer.utilityLayer);
        this.meshFaceEngine = new MeshFaceEngine(kbViewer);

        this.utilLayer = kbViewer.utilityLayer;

        this.attachedMesh = null;
    }

    attachTo(target: MeshNode) {
        this.__attachedNode = target;
        const aMesh = this.kbViewer.getRendererNode(target);
        if (!aMesh) {
            throw Error('Could not find mesh in scene to attach gizmo');
        }
        if (!(aMesh instanceof Mesh)) {
            throw Error('facet select gizmo must be attached to a mesh with geometry');
        }
        const mesh = aMesh;
        this.attachedMesh = mesh as Mesh;

        // Clean up old marquee
        if (this.disposeSubject) {
            this.disposeSubject.next();
            this.disposeSubject.complete();
        }

        this.disposeSubject = new Subject<void>();

        this.kbViewer.interaction.onFacetClick.pipe(takeUntil(this.disposeSubject)).subscribe(event => {
            if (!this.selectionsEnabled) {
                return;
            }
            event.selectedFaces.sort();

            if (event.ctrlKey) {
                // Add selections
                for (let i = 0; i < event.selectedFaces.length; i++) {
                    this.currentSelection.set(event.selectedFaces[i], null);
                }
            } else if (event.altKey) {
                // Remove selections
                for (let i = 0; i < event.selectedFaces.length; i++) {
                    this.currentSelection.delete(event.selectedFaces[i]);
                }
            } else {
                this.currentSelection.clear();
                for (let i = 0; i < event.selectedFaces.length; i++) {
                    this.currentSelection.set(event.selectedFaces[i], null);
                }
            }

            // Expand to coplanar faces if enabled (skip for alt/deselect)
            if (this.selectCoplanarEnabled && !event.altKey) {
                this.expandToCoplanarFaces(event.selectedFaces, mesh);
            }

            const result = this.getVertexGroups(this.currentSelection.keys());

            this.visualizeSubmesh(mesh, result, KbColor.Red());
            this.onSelectionChange.notifyObservers({ selectedFacets: result });
        });
    }

    updateGizmoPosition() {
        this.kbViewer.forceAndWaitForNextRender().then(() => {
            if (this.__attachedMesh) {
                const result = this.getVertexGroups(this.currentSelection.keys());
                this.visualizeSubmesh(this.__attachedMesh, result, KbColor.Red(), true);
            }
        });
    }

    selectFacetGroups(selection: IVertexGroup[]) {
        selection.forEach(vertexGroup => {
            if (vertexGroup.startIndex === undefined || vertexGroup.endIndex === undefined) {
                throw Error('corrupt vertex group');
            }
            const startFacet = Math.round(vertexGroup.startIndex / 3);
            const endFacet = Math.ceil(vertexGroup.endIndex / 3);

            for (let i = startFacet; i < endFacet; i++) {
                this.currentSelection.set(i, null);
            }
        });

        this.updateGizmoPosition();
    }

    enableSelectionChange() {
        this.selectionsEnabled = true;
        if (this.__attachedNode) {
            this.kbViewer.interaction
                .enableMarqueeSelect(this.__attachedNode, eMarqueeSelectMode.Facets)
                .pipe(takeUntil(this.disposeSubject))
                .subscribe(event => {
                    if (
                        this.selectionsEnabled &&
                        this.__attachedMesh &&
                        event.selectedFaces &&
                        event.selectedFaces.length > 0
                    ) {
                        if (event.ctrlKey) {
                            // Add selections
                            for (let i = 0; i < event.selectedFaces.length; i++) {
                                this.currentSelection.set(event.selectedFaces[i], null);
                            }
                        } else if (event.altKey) {
                            // Remove selections
                            for (let i = 0; i < event.selectedFaces.length; i++) {
                                this.currentSelection.delete(event.selectedFaces[i]);
                            }
                        } else {
                            this.currentSelection.clear();
                            for (let i = 0; i < event.selectedFaces.length; i++) {
                                this.currentSelection.set(event.selectedFaces[i], null);
                            }
                        }

                        // Expand to coplanar faces if enabled (skip for alt/deselect)
                        if (this.selectCoplanarEnabled && !event.altKey && event.selectedFaces) {
                            this.expandToCoplanarFaces(event.selectedFaces, this.__attachedMesh);
                        }

                        const result = this.getVertexGroups(this.currentSelection.keys());

                        this.visualizeSubmesh(this.__attachedMesh, result, KbColor.Red());
                        this.onSelectionChange.notifyObservers({ selectedFacets: result });
                    }
                });
        }
    }

    disableSelectionChange() {
        this.selectionsEnabled = false;
        this.kbViewer.interaction.disableMarqueeSelect();
    }

    /**
     * Disposes of the gizmo
     */
    dispose() {
        if (this.__attachedNode) {
            this.kbViewer.highlighter.remove(this.__attachedNode);
        }
        if (this.__attachedMesh) {
            const submeshInfo = dbSubmeshes.get(this.__attachedMesh.id);
            submeshInfo?.visualizationMesh.setEnabled(false);
        }
    }

    /**
     * CustomMeshes are not supported by this gizmo
     * @param mesh The mesh to replace the default mesh of the gizmo
     */
    setCustomMesh(mesh: Mesh) {
        Logger.Error('Custom meshes are not supported on this gizmo.');
    }

    setBackfaceCulling(enable: boolean) {
        this.backfaceCullingEnabled = enable;
        this.kbViewer.interaction.setSelectionCulling(enable);
    }

    setTolerance(tolerance: number) {
        this.selectionTolerance = tolerance;
        this.kbViewer.interaction.setSelectionTolerance(tolerance);
    }

    setSelectCoplanar(enabled: boolean) {
        this.selectCoplanarEnabled = enabled;
    }

    expandSelection(tolerance?: number) {
        if (this.attachedMesh instanceof Mesh) {
            const currentSelection = Array.from(this.currentSelection.keys());
            const newSelection = this.meshFaceEngine.expandSelection(currentSelection, this.attachedMesh, tolerance);

            for (let i = 0; i < newSelection.length; i++) {
                this.currentSelection.set(newSelection[i], null);
            }
            const result = this.getVertexGroups(this.currentSelection.keys());

            this.visualizeSubmesh(this.attachedMesh, result, KbColor.Red());
            this.onSelectionChange.notifyObservers({ selectedFacets: result });
        }
    }

    setVisualization(opacity: number) {
        const submeshInfo = dbSubmeshes.get(this.attachedMesh!.id);
        if (submeshInfo && submeshInfo.visualizationMesh.material instanceof MultiMaterial) {
            submeshInfo.highlightMaterial.alpha = opacity;
        }
        this.highlightOpacity = opacity;
    }

    /**
     * Given a set of seed face IDs, finds all other faces in the mesh that
     * lie on the same plane (matching normal direction and plane offset).
     *
     * Respects backface culling: when enabled, only faces whose normal
     * points toward the camera are included and opposite-facing coplanar
     * faces are skipped.
     */
    private expandToCoplanarFaces(seedFaceIds: number[], mesh: Mesh): void {
        const indices = mesh.getIndices();
        const positions = mesh.getVerticesData(VertexBuffer.PositionKind);
        if (!indices || !positions) {
            return;
        }

        const totalFaces = indices.length / 3;

        // Use the UI tolerance for the normal/distance comparison.
        // Clamp to a small epsilon so that a tolerance of 0 still handles
        // floating-point imprecision.
        const epsilon = Math.max(this.selectionTolerance, 1e-4);

        // Build reference planes from the seed faces
        const referencePlanes = seedFaceIds.map(faceId => this.getFacePlane(faceId, indices, positions));

        // When backface culling is enabled we need the camera forward vector
        // in the mesh's local space so we can reject faces that point away.
        let cameraForwardLocal: Vector3 | null = null;
        if (this.backfaceCullingEnabled) {
            const camera = this.scene.activeCamera;
            if (camera) {
                const cameraForward = camera.getForwardRay().direction;
                const invWorld = mesh.getWorldMatrix().clone().invert();
                cameraForwardLocal = Vector3.TransformNormal(cameraForward, invWorld);
                cameraForwardLocal.normalize();
            }
        }

        for (let faceId = 0; faceId < totalFaces; faceId++) {
            // Skip faces that are already selected
            if (this.currentSelection.has(faceId)) {
                continue;
            }

            const candidate = this.getFacePlane(faceId, indices, positions);

            // Backface culling: skip faces whose normal points away from camera
            if (cameraForwardLocal) {
                // A face is back-facing when its normal has a positive dot
                // product with the camera's forward (look) direction.
                if (Vector3.Dot(candidate.normal, cameraForwardLocal) > 0) {
                    continue;
                }
            }

            for (const ref of referencePlanes) {
                const dot = Vector3.Dot(candidate.normal, ref.normal);

                // Check that the normals are parallel.
                // With backface culling we require the same winding (dot ≈ 1).
                // Without it we accept opposite winding too (|dot| ≈ 1).
                const isParallel = this.backfaceCullingEnabled
                    ? dot >= 1 - epsilon
                    : Math.abs(dot) >= 1 - epsilon;

                if (!isParallel) {
                    continue;
                }

                // Check that both faces lie on the same plane.
                // When normals are opposite the plane constant is negated.
                const effectiveD = dot < 0 ? -candidate.d : candidate.d;

                if (Math.abs(effectiveD - ref.d) <= epsilon) {
                    this.currentSelection.set(faceId, null);
                    break; // No need to check remaining reference planes
                }
            }
        }
    }

    /**
     * Computes the unit normal and plane constant (d = n·v0) for a face.
     */
    private getFacePlane(
        faceId: number,
        indices: ReturnType<Mesh['getIndices']> & {},
        positions: ReturnType<Mesh['getVerticesData']> & {}
    ): { normal: Vector3; d: number } {
        const idx = faceId * 3;
        const i0 = indices[idx];
        const i1 = indices[idx + 1];
        const i2 = indices[idx + 2];

        const v0 = Vector3.FromArray(positions, i0 * 3);
        const v1 = Vector3.FromArray(positions, i1 * 3);
        const v2 = Vector3.FromArray(positions, i2 * 3);

        const edge1 = v1.subtract(v0);
        const edge2 = v2.subtract(v0);
        const normal = Vector3.Cross(edge1, edge2);
        normal.normalize();

        const d = Vector3.Dot(normal, v0);

        return { normal, d };
    }

    private getVertexGroups(faceIds: IterableIterator<number>): Array<Required<IVertexGroup>> {
        const result: Array<Required<IVertexGroup>> = [];

        for (const faceId of faceIds) {
            if (result.length > 0 && result[result.length - 1].endIndex === faceId * 3) {
                result[result.length - 1].endIndex += 3;
            } else {
                result.push({
                    startIndex: faceId * 3,
                    endIndex: (faceId + 1) * 3,
                });
            }
        }

        return result;
    }

    private visualizeSubmesh(
        node: Mesh,
        vertices: IVertexGroup[],
        color?: KbColor | null,
        geometryNeedsUpdate?: boolean
    ) {
        if (node instanceof Mesh && node.geometry) {
            let submeshInfo = dbSubmeshes.get(node.id);
            let highlightMaterial: StandardMaterial | undefined;
            const utilityLayerScene = this.utilLayer.utilityLayerScene;

            if (submeshInfo) {
                highlightMaterial = submeshInfo.highlightMaterial;
            }

            if (submeshInfo && geometryNeedsUpdate) {
                submeshInfo.visualizationMesh.dispose();
                submeshInfo = undefined;
            }

            if (!submeshInfo) {
                const multiMaterial = new MultiMaterial(`submeshVisualizationMat_${node.id}`, utilityLayerScene);

                const transparantMaterial = new StandardMaterial(
                    `submeshVisualizationMat1_${node.id}`,
                    utilityLayerScene
                );
                transparantMaterial.alpha = 0;
                multiMaterial.subMaterials.push(transparantMaterial);

                if (!highlightMaterial) {
                    highlightMaterial = new StandardMaterial(`submeshVisualizationMat2_${node.id}`, utilityLayerScene);
                    highlightMaterial.emissiveColor = color?.toColor3() || KbColor.KbmaxOrange().toColor3();
                    highlightMaterial.alpha = this.highlightOpacity;
                }
                multiMaterial.subMaterials.push(highlightMaterial);

                submeshInfo = {
                    visualizationMesh: new Mesh(`submeshVisualization_${node.id}`, utilityLayerScene, null, node, true),
                    highlightMaterial,
                    vertices,
                };

                submeshInfo.visualizationMesh.makeGeometryUnique();
                submeshInfo.visualizationMesh.material = multiMaterial;
                submeshInfo.visualizationMesh.isPickable = false;

                dbSubmeshes.set(node.id, submeshInfo);
            } else {
                submeshInfo.visualizationMesh.setEnabled(true);
            }

            const vMesh = submeshInfo.visualizationMesh;
            const totalIndices = node.geometry.getTotalIndices();
            const totalVertices = node.geometry.getTotalVertices();
            vMesh.subMeshes = [];
            new SubMesh(0, 0, totalVertices, 0, totalIndices, vMesh);

            vertices.forEach(indexGroup => {
                if (indexGroup.startIndex === undefined || indexGroup.endIndex === undefined) {
                    throw Error('Missing submesh information');
                }
                new SubMesh(
                    1,
                    0,
                    totalVertices,
                    indexGroup.startIndex,
                    indexGroup.endIndex - indexGroup.startIndex,
                    vMesh
                );
            });
        }
    }
}
